import React, { useState } from 'react';
import { styled} from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Button, Tooltip } from '@mui/material';
import { Paper } from '@mui/material';
import PopMenu from './PopMenu';
import { CgClose } from 'react-icons/cg';
import { FaEdit } from 'react-icons/fa';

const NormalListTable = ({row,heading,handleEditTransaction,task,config,openPopup, editClick,deleteMailConfig,editMailConfig}) => {
    // State for sorting
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('');
    const [sortedRows, setSortedRows] = useState(row);

    // Get sortable fields based on the data
    const getSortableFields = () => {
        if (!row || row.length === 0) return [];
        
        if (task) {
            // For tasks, exclude menu and uniqueId
            return Object.keys(row[0]).filter(key => 
                key !== 'menu' && key !== 'uniqueId' && key !== 'item'
            );
        } else if (config) {
            // For config, exclude menu and id fields
            return Object.keys(row[0]).filter(key => 
                key !== 'menu' && key !== 'id' && key !== 'list'
            );
        } else {
            // For transactions, exclude menu
            return Object.keys(row[0]).filter(key => 
                key !== 'menu'
            );
        }
    };

    // Function to handle sort request
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        const newOrder = isAsc ? 'desc' : 'asc';
        setOrder(newOrder);
        setOrderBy(property);
        
        // Sort the rows
        const sortedData = [...row].sort((a, b) => {
            // Handle string comparison
            if (typeof a[property] === 'string' && typeof b[property] === 'string') {
                if (newOrder === 'asc') {
                    return a[property].localeCompare(b[property]);
                } else {
                    return b[property].localeCompare(a[property]);
                }
            } 
            // Handle number comparison
            else {
                if (newOrder === 'asc') {
                    return a[property] - b[property];
                } else {
                    return b[property] - a[property];
                }
            }
        });
        
        setSortedRows(sortedData);
    };

    // Use sortedRows if we have sorting, otherwise use the original rows
    const displayRows = orderBy ? sortedRows : row;

    const tableHeading = heading;
    const menuOptions = ['Edit', 'Delete'];

    // Get sortable fields
    const sortableFields = getSortableFields();

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.common.black,
          padding: config?'4px':'8px',
        },
        [`&.${tableCellClasses.body}`]: {
          fontSize: 16, 
          padding: config?'2px 5px': '2px 10px',
          fontFamily: 'Montserrat,sans-serif',
          fontWeight: 600
        },
    }));
      
    const StyledTableRow = styled(TableRow)(({ theme }) => ({
        '&:nth-of-type(odd)': {
          backgroundColor: '#F3FBFF',
        },
        '&:nth-of-type(even)': {
          backgroundColor: '#FFFFFF',
        },
        // hide last border
        '&:last-child td': {
          border: 0,
        },
        height: config? '35px':'45px',
        alignContent:'center'
    }));

    const cloneClick=()=>{
    }

    const EmptyTableMessage = () => (
      <div style={{
        display: 'flex',
        justifyContent:'center',
        alignItems: 'center',
        fontSize: '22px',
        width:'100%',
        height:'auto'
      }}>
        <p>{config ?"No configurations found...": "No Tasks found..."}</p>
      </div>
    );

  return (
    <>
      <TableContainer
        component={Paper}
        style={{ 
          border: !config ?"1px solid #cfd4e9" : "1px solid #cfd4e9", 
          width: config?"100%":"96%", 
          margin: config ? "0px": "20px 30px", 
          height: 'auto',
          maxHeight: config ? '75%':'80%'
        }}
      >
        { row.length === 0 ? (
              <EmptyTableMessage />
        ) : (
          <Table sx={{ minWidth: config ? 500 : 700 }} aria-label="customized table">
            <TableHead>
              <TableRow>
                {tableHeading.map((heading, index) => {
                  // Get corresponding field key based on index
                  const headKey = row.length > 0 ? Object.keys(row[0])[index] : '';
                  const isSortable = sortableFields.includes(headKey) && headKey !== 'sNo';
                  
                  return (
                    <StyledTableCell key={index} style={{ color: "#757ea5" }}>
                      {isSortable ? (
                        <TableSortLabel
                          active={orderBy === headKey}
                          direction={orderBy === headKey ? order : 'asc'}
                          onClick={() => handleRequestSort(headKey)}
                          sx={{ 
                            color: "#757ea5",
                            '&.MuiTableSortLabel-active': {
                              color: "#02ACF6",
                            },
                          }}
                        >
                          {heading}
                        </TableSortLabel>
                      ) : (
                        heading
                      )}
                    </StyledTableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((item, index) => (
                <StyledTableRow key={`row-${index}`} style={{border: '1px solid #cfd4e9'}}>
                  {task ?
                    Object.keys(item).slice(0,-2).map((key, index) => (
                      key === 'publish' ?
                        <StyledTableCell key={key}>
                          <Button size="small" variant="contained"
                            sx={{
                              textTransform: 'none',
                              width: '110px',
                              height:'25px',
                              cursor: item[key] === "Publish" ? 'pointer' : 'not-allowed',
                              backgroundColor: item[key] === "Publish" ? '#02ACF6' : '#F6F7FC',
                              color: item[key] === "Publish" ? '#FFFFFF' : '#757EA5',
                              border:item[key] === "Publish" ? '1px solid #02ACF6':'1px solid #757EA5',
                            }}
                            onClick={item[key] === "Publish" ? () => openPopup('publish', item.uniqueId, 'task', item.taskName) : null}
                          >
                            {item[key]}
                          </Button>
                        </StyledTableCell> :
                        key === 'menu' ?
                          <StyledTableCell key={key}>
                            <PopMenu 
                              menuOption={menuOptions} 
                              cloneClick={cloneClick} 
                              deleteClick={() => openPopup('delete', item.uniqueId, 'task', item.taskName)} 
                              editClick={() => editClick(item.uniqueId, item.item)} 
                              index={index} 
                            />
                          </StyledTableCell> : 
                          <StyledTableCell 
                            key={key}                        
                            onClick={key === 'taskName' ? () => {editClick(item.uniqueId, item.item)} : null}
                            style={{ 
                              color: key === 'taskName' ? '#02ACF6' : '#121B45',
                              cursor: key === 'taskName' ? 'pointer' : 'auto'
                            }}
                          >
                            {item[key]}
                          </StyledTableCell>
                    )) :
                    config ?
                      Object.keys(item).slice(0,-2).map((key, index) => (
                        key === 'menu' ?
                          <StyledTableCell key={key}>
                            <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                              <Tooltip title='edit config'>
                                <FaEdit style={{cursor:'pointer'}} onClick={() => editMailConfig(item.list)}/>
                              </Tooltip>
                              <Tooltip title='delete config'>
                                <CgClose style={{cursor:'pointer'}} onClick={() => deleteMailConfig(item.id, item.name)}/>
                              </Tooltip>
                            </div>
                          </StyledTableCell> : 
                          <StyledTableCell 
                            key={key}                        
                            onClick={null}
                            style={{ 
                              color: key === 'name' ? '#02ACF6' : '#121B45',
                              cursor: key === 'name' ? 'pointer' : 'auto'
                            }}
                          >
                            {item[key]}
                          </StyledTableCell>
                      )) :
                      Object.keys(item).map((key, index) => (
                        key === 'menu' ?
                          <StyledTableCell key={key}>
                            <PopMenu 
                              menuOption={menuOptions} 
                              cloneClick={cloneClick} 
                              index={index} 
                            />
                          </StyledTableCell> : 
                          <StyledTableCell 
                            key={key}                        
                            onClick={key === 'transactionName' ? () => {handleEditTransaction()} : null}
                            style={{ 
                              color: key === 'transactionName' ? '#02ACF6' : '#121B45',
                              cursor: key === 'transactionName' ? 'pointer' : 'auto'
                            }}
                          >
                            {item[key]}
                          </StyledTableCell>
                      ))
                  }
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
}

export default NormalListTable